import React from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { PageHeader, Section } from "@/components/Primitives";
import QuoteForm from "@/components/QuoteForm";
import { CITIES, SERVICES, NEARBY, BUSINESS, scSlug } from "@/data/config";
import { ArrowUpRight } from "lucide-react";

const CITY_IMG =
  "https://images.unsplash.com/photo-1726508670667-39e52fd5c5c8?crop=entropy&cs=srgb&fm=jpg&w=1400";

export default function City() {
  const { pathname } = useLocation();
  const citySlug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const city = CITIES.find((c) => c.slug === citySlug);
  if (!city) return <Navigate to="/" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "RoofingContractor",
        "@context": "https://schema.org",
        name: `${BUSINESS.name} — ${city.name}`,
        url: `${BUSINESS.siteUrl}/${city.slug}`,
        telephone: BUSINESS.phone,
        areaServed: { "@type": "City", name: `${city.name}, WA` },
        geo: { "@type": "GeoCoordinates", latitude: city.lat, longitude: city.lng },
      },
      buildBreadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: city.name, path: `/${city.slug}` },
      ]),
    ],
  };

  const nearby = (NEARBY[city.slug] || []).map((cs) => CITIES.find((c) => c.slug === cs));

  return (
    <>
      <SEO
        title={city.title}
        description={city.meta}
        path={`/${city.slug}`}
        image={CITY_IMG}
        jsonLd={jsonLd}
      />
      <PageHeader
        overline={`Service Area / ${city.accent}`}
        title={`${city.name} gutters and roofing.`}
        lede={city.intro}
        image={CITY_IMG}
        alt={`${city.name}, Washington landscape`}
      />

      <Section testid="city-weather">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <p className="overline mb-4">Local weather angle</p>
            <h2 className="font-display text-3xl md:text-5xl leading-[1.02] tracking-tightest">
              What {city.name} throws at a roof.
            </h2>
          </div>
          <div className="md:col-span-8 md:pl-10">
            <p className="text-bone-100 text-lg md:text-xl leading-relaxed max-w-2xl">
              {city.weatherAngle}
            </p>
            {city.neighborhoods?.length > 0 && (
              <div className="mt-10">
                <p className="overline mb-4">Neighborhoods we cover</p>
                <div className="flex flex-wrap gap-2">
                  {city.neighborhoods.map((n) => (
                    <span
                      key={n}
                      className="text-sm text-bone-200 border border-white/10 rounded-sm px-3 py-2"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section testid="city-services" className="bg-ink-900 border-y border-white/10">
        <p className="overline mb-4">Every service in {city.name}</p>
        <h2 className="font-display text-3xl md:text-5xl tracking-tightest mb-10">
          Five services running out of {city.name} every week.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="bg-ink-950"
            >
              <Link
                to={`/${scSlug(city.slug, s.slug)}`}
                data-testid={`city-service-${s.slug}`}
                className="group block p-6 md:p-8 hover:bg-ink-900 transition-colors h-full"
              >
                <div className="flex justify-between items-start mb-6">
                  <span className="font-display text-4xl text-copper-500 leading-none">
                    {s.accent}
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-bone-400 group-hover:text-copper-500 group-hover:rotate-45 transition-transform" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl tracking-tightest mb-2">
                  {city.name} {s.name}
                </h3>
                <p className="text-bone-300 text-sm">{s.short}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Nearby cities — for orphan-free crawl */}
      <Section testid="city-nearby">
        <p className="overline mb-4">Nearby cities we cover</p>
        <h2 className="font-display text-3xl md:text-5xl tracking-tightest mb-10">
          Also serving these areas near {city.name}.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {nearby.map((n) => (
            <Link
              key={n.slug}
              to={`/${n.slug}`}
              data-testid={`city-nearby-${n.slug}`}
              className="group bg-ink-950 hover:bg-ink-900 p-6 md:p-8 flex justify-between items-center"
            >
              <span className="font-display text-2xl md:text-3xl tracking-tightest text-bone-50 group-hover:text-copper-500">
                {n.name}
              </span>
              <ArrowUpRight className="w-5 h-5 text-bone-400 group-hover:text-copper-500 group-hover:rotate-45 transition-transform" />
            </Link>
          ))}
        </div>
      </Section>

      <Section testid="city-quote" className="bg-ink-900 border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="overline mb-4">Free quote in {city.name}</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tightest">
              Book a walk around at your {city.name} home.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm preselectedCity={city.name} />
          </div>
        </div>
      </Section>
    </>
  );
}
